import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Modal,
  ModalSizes,
  ButtonVariants,
  Stepper,
  Table,
  Alert,
  AlertVariants,
  Dropdown,
  Typography,
  TypographyVariants,
  TypographyColors,
  TypographyElements,
} from '@carboledger/ui';
import { useTranslations, UnitTypeContext, useUnitTypeHeader, resolveRowUnitType } from '@/hooks';
import { SummaryCard } from '../SummaryCard';
import { DiscrepancyModal } from '../Discrepancy';
import { rowUnitSymbol } from '@/lib/mappers/quantities';
import { buildTableLabels } from '@/config/tableLabels';
import { orderUnits, shipmentUnits } from './linkUnits';
import {
  buildLinkOrdersStep1Columns,
  buildLinkOrdersStep2Columns,
  buildLinkShipmentsStep1Columns,
  buildLinkShipmentsStep2Columns,
  buildLinkShipmentsAssignColumns,
} from './linkOrdersColumns';
import { assignedTotalInUnit, buildOrderPanelFields, buildShipmentPanelFields } from './linkFields';
import {
  buildLinkableOrderDiscrepancySections,
  buildLinkableOrderDiscrepancyMessage,
} from './linkOrdersDiscrepancy';
import { EMPTY_LIST } from '@/lib/utils';
import { buildLinkedGroups, getLinkedGroup } from '../pickerGroups';
import styles from './LinkOrdersModal.module.scss';

// Which side ends up singular; see the folder CLAUDE.md for the four flows.
export const LINK_ORDER_FLOWS = Object.freeze({
  shipmentToOrders: 'shipmentToOrders',
  orderToShipments: 'orderToShipments',
  shipmentsToOrder: 'shipmentsToOrder',
  ordersToShipment: 'ordersToShipment',
});

// Opened from a list toolbar with rows already ticked: step 1 picks ONE of the other side.
const BULK_FLOWS = [LINK_ORDER_FLOWS.shipmentsToOrder, LINK_ORDER_FLOWS.ordersToShipment];

// The two whose step 1 lists outgoing shipments rather than orders.
const SHIPMENT_STEP1_FLOWS = [LINK_ORDER_FLOWS.orderToShipments, LINK_ORDER_FLOWS.ordersToShipment];

export const isBulkOrderFlow = (flow) => BULK_FLOWS.includes(flow);
const picksShipments = (flow) => SHIPMENT_STEP1_FLOWS.includes(flow);

const SELECT_STEP = 0;
const ASSIGN_STEP = 1;
const getRowId = (row) => row.id;

/**
 * Props:
 *   open          whether the modal is shown
 *   onClose       close handler (✕ / Cancel / Escape)
 *   title         the modal's heading, already carrying the id(s)
 *   detailFields  (unitType) => GENERAL DETAILS card rows, so the card reads in the
 *                 unit the dropdown below it is on
 *   candidates    the rows step 1 lists — orders, or outgoing shipments
 *   loading       whether the candidate list is still fetching
 *   flow          one of LINK_ORDER_FLOWS
 *   bulkRows      (the bulk flows) the rows the list had ticked, which step 2 assigns against
 *   unassignedByUnit  (the single flows) the parent's still-unassigned amounts, raw, per unit
 *   isSaving      disables Save while the mutation is in flight
 *   onSearchChange (term) => void — the list is filtered server-side
 *   onSortChange   (sort) => void — the list is ordered server-side
 *   onSave        (entries, pickedId) => void — entries are `{ rowId, quantity, unitSymbol }`
 */
export const LinkOrdersModal = ({
  open,
  onClose,
  title,
  detailFields,
  candidates = EMPTY_LIST,
  loading = false,
  flow = LINK_ORDER_FLOWS.shipmentToOrders,
  bulkRows = EMPTY_LIST,
  unassignedByUnit,
  isSaving = false,
  onSearchChange,
  onSortChange,
  onSave,
}) => {
  const t = useTranslations();
  const [step, setStep] = useState(SELECT_STEP);
  const [selectedIds, setSelectedIds] = useState(EMPTY_LIST);
  const [pickedId, setPickedId] = useState(null);
  const [assignments, setAssignments] = useState({});
  // The Table's key — it owns its search, selection and page, so only a remount clears them.
  const [session, setSession] = useState(0);
  const [discrepancyRow, setDiscrepancyRow] = useState(null);
  const [error, setError] = useState(null);

  // Bulk picks ONE row for the ticked rows; the other flows tick many for one parent.
  const isBulk = isBulkOrderFlow(flow);
  const step1IsShipments = picksShipments(flow);

  const picked = useMemo(
    () => (isBulk ? (candidates.find((row) => row.id === pickedId) ?? null) : null),
    [isBulk, candidates, pickedId],
  );

  // Step 2's rows: the ticked list rows in a bulk flow, else whatever step 1 selected.
  const step1Rows = candidates;
  // Every step-2 frame draws Remove, so a dropped assignment drops the row in both flows.
  const step2Rows = useMemo(() => {
    const rows = isBulk ? bulkRows : candidates.filter((row) => selectedIds.includes(row.id));
    return rows.filter((row) => assignments[row.id] !== undefined);
  }, [isBulk, bulkRows, candidates, selectedIds, assignments]);

  const unitRows = step === SELECT_STEP ? step1Rows : step2Rows;
  // Step 2 of a bulk flow tables the OTHER side, so the header offers that side's bases.
  const unitOptions =
    step === ASSIGN_STEP && isBulk && step1IsShipments
      ? shipmentUnits.buildUnitHeaderOptions
      : orderUnits.buildUnitHeaderOptions;
  const { unitHeaderSelect, contextValue, selections } = useUnitTypeHeader(
    unitRows,
    unitOptions,
    t,
  );

  // The builder asks in the basis it renders, which is not always the header's.
  const assignedInUnit = useCallback(
    (unit) => assignedTotalInUnit(step2Rows, assignments, selections, unit),
    [step2Rows, assignments, selections],
  );

  // The card reads in whatever unit the header dropdown is on, exactly as the table does.
  const detailRows = useMemo(
    () => detailFields?.(unitHeaderSelect.value, assignedInUnit) ?? EMPTY_LIST,
    [detailFields, unitHeaderSelect.value, assignedInUnit],
  );

  useEffect(() => {
    if (!open) return;
    setStep(SELECT_STEP);
    setSelectedIds(EMPTY_LIST);
    setPickedId(null);
    setAssignments({});
    setError(null);
    setSession((previous) => previous + 1);
    setDiscrepancyRow(null);
    onSearchChange?.('');
    // onSearchChange is the parent's setter; re-running on its identity would loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleClose = useCallback(() => onClose?.(), [onClose]);
  const handleSelectionChange = useCallback((rows, ids) => setSelectedIds(ids), []);
  const handlePick = useCallback((id) => setPickedId(id), []);
  const handleSearchChange = useCallback((value) => onSearchChange?.(value), [onSearchChange]);
  const handleSortChange = useCallback((next) => onSortChange?.(next), [onSortChange]);
  const handleOpenDiscrepancy = useCallback((row) => setDiscrepancyRow(row), []);
  const handleCloseDiscrepancy = useCallback(() => setDiscrepancyRow(null), []);

  const handleAssign = useCallback((rowId, quantity) => {
    setAssignments((previous) => ({ ...previous, [rowId]: quantity }));
  }, []);

  const handleRemove = useCallback((rowId) => {
    setAssignments((previous) => {
      const next = { ...previous };
      delete next[rowId];
      return next;
    });
  }, []);

  // Seeding a blank assignment per row is also what puts a bulk row on step 2.
  const goNext = useCallback(() => {
    const rows = isBulk ? bulkRows : candidates.filter((row) => selectedIds.includes(row.id));
    // Only the typed quantity is state; the unit follows the row's own selection.
    setAssignments(Object.fromEntries(rows.map((row) => [row.id, ''])));
    setError(null);
    setStep(ASSIGN_STEP);
  }, [isBulk, bulkRows, candidates, selectedIds]);

  const goBack = useCallback(() => {
    setStep(SELECT_STEP);
    setError(null);
  }, []);

  const unitType = unitHeaderSelect.value;
  // The singular side's ceiling: the picked row in a bulk flow, else the page's own parent.
  const parentUnassigned = isBulk
    ? (picked?.unassignedByUnit?.[unitType] ?? null)
    : (unassignedByUnit?.[unitType] ?? null);

  const entries = useMemo(
    () =>
      step2Rows.map((row) => ({
        rowId: row.id,
        quantity: assignments[row.id],
        unitSymbol: rowUnitSymbol(row, resolveRowUnitType(row, selections)),
      })),
    [step2Rows, assignments, selections],
  );

  const assignedTotal = useMemo(
    () => assignedTotalInUnit(step2Rows, assignments, selections, unitType),
    [step2Rows, assignments, selections, unitType],
  );

  const handleSave = useCallback(() => {
    const filled = entries.filter((entry) => {
      const amount = Number(entry.quantity);
      return entry.quantity !== '' && Number.isFinite(amount) && amount > 0;
    });

    if (filled.length === 0) {
      setError(t('LINK_ASSIGN_REQUIRED'));
      return;
    }
    const missingUnit = filled.find((entry) => !entry.unitSymbol);
    if (missingUnit) {
      setError(t('LINK_SHIPMENTS_ASSIGN_UNIT_MISSING'));
      return;
    }
    if (parentUnassigned != null && assignedTotal > parentUnassigned) {
      setError(step1IsShipments ? t('LINK_ORDERS_OVER_ORDER') : t('LINK_ORDERS_OVER_SHIPMENT'));
      return;
    }

    setError(null);
    onSave?.(filled, isBulk ? pickedId : null);
  }, [entries, assignedTotal, parentUnassigned, step1IsShipments, isBulk, pickedId, onSave, t]);

  const steps = useMemo(
    () => [
      {
        label: step1IsShipments
          ? t('LINK_SHIPMENTS_STEP_SELECT_OUTGOING')
          : t('LINK_ORDERS_STEP_SELECT'),
      },
      { label: t('LINK_STEP_ASSIGN') },
    ],
    [t, step1IsShipments],
  );

  const step1Columns = useMemo(() => {
    const build = step1IsShipments ? buildLinkShipmentsStep1Columns : buildLinkOrdersStep1Columns;
    return build(t, {
      headerSelect: unitHeaderSelect,
      selectMode: isBulk ? 'single' : 'multi',
      selectedId: pickedId,
      onSelect: handlePick,
      onOpenDiscrepancy: handleOpenDiscrepancy,
    });
  }, [t, step1IsShipments, isBulk, pickedId, handlePick, unitHeaderSelect, handleOpenDiscrepancy]);

  // Step 2 lists the OTHER side in a bulk flow — the rows the list ticked.
  const step2Columns = useMemo(() => {
    const listsShipments = isBulk ? !step1IsShipments : step1IsShipments;
    // The one-order flow is the only frame that puts Assign second; see the builder's note.
    const buildShipments = isBulk
      ? buildLinkShipmentsStep2Columns
      : buildLinkShipmentsAssignColumns;
    const build = listsShipments ? buildShipments : buildLinkOrdersStep2Columns;
    return build(t, {
      headerSelect: unitHeaderSelect,
      assignments,
      onAssign: handleAssign,
      onRemove: handleRemove,
      onOpenDiscrepancy: handleOpenDiscrepancy,
    });
  }, [
    t,
    isBulk,
    step1IsShipments,
    unitHeaderSelect,
    assignments,
    handleAssign,
    handleRemove,
    handleOpenDiscrepancy,
  ]);

  /**
   * ONE card, exactly as the inbound wizard renders one: the selection's General details,
   * except in a bulk step 2, where the design replaces it with the picked row's own.
   */
  const card = useMemo(() => {
    if (step === ASSIGN_STEP && isBulk) {
      if (!picked) return null;
      return step1IsShipments
        ? {
            heading: t('SECTION_SHIPMENT_DETAILS'),
            fields: buildShipmentPanelFields(t, picked, unitHeaderSelect.value, assignedInUnit),
          }
        : {
            heading: t('SECTION_ORDER_DETAILS'),
            fields: buildOrderPanelFields(t, picked, unitHeaderSelect.value, assignedInUnit),
          };
    }
    return { heading: t('SECTION_GENERAL_DETAILS'), fields: detailRows };
  }, [
    step,
    isBulk,
    picked,
    step1IsShipments,
    t,
    unitHeaderSelect.value,
    assignedInUnit,
    detailRows,
  ]);

  // The notice names the side step 1 lists, as each inbound wizard names its own.
  const noneLabels = step1IsShipments
    ? { title: 'LINK_OUTGOING_SHIPMENTS_NONE_TITLE', body: 'LINK_OUTGOING_SHIPMENTS_NONE_BODY' }
    : { title: 'LINK_ORDERS_NONE_TITLE', body: 'LINK_ORDERS_NONE_BODY' };

  const emptyState = useMemo(
    () => ({ title: t(step1IsShipments ? 'LINK_OUTGOING_SHIPMENTS_EMPTY' : 'LINK_ORDERS_EMPTY') }),
    [t, step1IsShipments],
  );
  const tableLabels = useMemo(() => buildTableLabels(t), [t]);
  // Order candidates say whether they're already linked; shipment candidates can't yet (no per-order data).
  const step1Groups = useMemo(
    () =>
      step1IsShipments
        ? EMPTY_LIST
        : buildLinkedGroups(t, {
            linkedKey: 'PICKER_GROUP_LINKED_ORDERS',
            otherKey: 'PICKER_GROUP_OTHER_ORDERS',
          }),
    [step1IsShipments, t],
  );
  const getStep1RowGroup = step1IsShipments ? undefined : getLinkedGroup;

  const canAdvance = isBulk ? pickedId != null : selectedIds.length > 0;

  const buttons = useMemo(() => {
    if (step === SELECT_STEP) {
      return [
        { label: t('COMMON_CANCEL'), variant: ButtonVariants.neutral, onClick: handleClose },
        {
          label: t('COMMON_NEXT'),
          variant: ButtonVariants.primary,
          disabled: !canAdvance,
          onClick: goNext,
        },
      ];
    }
    return [
      { label: t('COMMON_PREVIOUS'), variant: ButtonVariants.ghost, onClick: goBack },
      { label: t('COMMON_CANCEL'), variant: ButtonVariants.neutral, onClick: handleClose },
      {
        label: t('COMMON_SAVE'),
        variant: ButtonVariants.primary,
        disabled: isSaving,
        loading: isSaving,
        onClick: handleSave,
      },
    ];
  }, [t, step, canAdvance, handleClose, goNext, goBack, handleSave, isSaving]);

  return (
    <Modal
      open={open}
      onClose={handleClose}
      size={ModalSizes.large}
      title={title}
      buttons={buttons}
    >
      <div className={styles.body}>
        <Stepper steps={steps} activeStep={step} variant="flush" className={styles.stepper} />

        {card ? <SummaryCard heading={card.heading} fields={card.fields} /> : null}

        {error ? <Alert variant={AlertVariants.danger}>{error}</Alert> : null}

        {/* TODO: use the library Tooltip once it ships. */}
        {!loading && step === SELECT_STEP && step1Rows.length === 0 ? (
          <Alert variant={AlertVariants.warning} title={t(noneLabels.title)}>
            {t(noneLabels.body)}
          </Alert>
        ) : null}

        <UnitTypeContext.Provider value={contextValue}>
          {step === SELECT_STEP ? (
            <Table
              key={`select-${session}`}
              columns={step1Columns}
              data={step1Rows}
              loading={loading}
              getRowId={getRowId}
              searchable
              onSearchChange={handleSearchChange}
              onSortChange={handleSortChange}
              selectable={!isBulk}
              onSelectionChange={handleSelectionChange}
              emptyState={emptyState}
              labels={tableLabels}
              groups={step1Groups}
              getRowGroup={getStep1RowGroup}
            />
          ) : (
            <>
              {!isBulk ? (
                <div className={styles.unitField}>
                  <Typography
                    as={TypographyElements.span}
                    variant={TypographyVariants.bodyXs}
                    color={TypographyColors.secondary}
                  >
                    {t('LINK_ASSIGN_UNIT_LABEL')}
                  </Typography>
                  {/* The same set-every-row control the unit column's header uses. */}
                  <Dropdown
                    value={unitHeaderSelect.value}
                    onChange={unitHeaderSelect.onChange}
                    options={unitHeaderSelect.options}
                    aria-label={t('LINK_ASSIGN_UNIT_LABEL')}
                  />
                </div>
              ) : null}
              <Table
                key={`assign-${session}`}
                columns={step2Columns}
                data={step2Rows}
                getRowId={getRowId}
                showPagination={false}
                emptyState={emptyState}
                labels={tableLabels}
              />
            </>
          )}
        </UnitTypeContext.Provider>
      </div>

      <DiscrepancyModal
        open={discrepancyRow != null}
        onClose={handleCloseDiscrepancy}
        referenceId={discrepancyRow?.orderNo ?? discrepancyRow?.shipmentId}
        message={buildLinkableOrderDiscrepancyMessage(t, discrepancyRow)}
        sections={buildLinkableOrderDiscrepancySections(t, discrepancyRow)}
      />
    </Modal>
  );
};
